import '../styles/main.css';
import * as THREE from 'three';
import { params, DEFAULT_HEIGHTMAP_URL } from './config.js';
import {
  scene,
  initScene,
  applyYawPitchDeg,
  applyFogFromParams,
  setDefaultCameraState,
  setupResizeHandler
} from './scene.js';
import * as SceneModule from './scene.js';
import { airplaneState } from './airplane/state.js';
import { initAirplane, getAirplaneGroup } from './airplane/create.js';
import { updateAirplane, setupCrashOverlayHandler } from './airplane/update.js';
import { pointMaterial } from './terrain/material.js';
import { state, scrollOffset, rebuildTiling, updateTileMatrices, updateTileLOD, getTilesPoints } from './terrain/tiling.js';
import { applyHeightmapTexture, setupHeightmapHandlers, sampleTerrainHeight } from './terrain/heightmap.js';
import { radarState, updateRadarPulse, updateVisualRings } from './effects/radar.js';
import { updateSonicBoom } from './effects/sonicBoom.js';
import { updateCameraMode } from './camera/modes.js';
import { setupKeyboardControls } from './controls/keyboard.js';
import { setupMouseControls, mouseNdc } from './controls/mouse.js';
import { setupTouchControls } from './controls/touch.js';
import { setupGyroControls } from './controls/gyro.js';
import { setupTweakpane, pane } from './ui/tweakpane.js';
import { initBoostUI, updateBoostUI } from './ui/boost.js';
import { getAudioEnergy, isAudioReactiveActive, getAnalyzerDebugInfo } from './audio/analyzer.js';
import { initComposer, getComposer, resizeComposer, renderSelectiveBloom } from './effects/composer.js';

// Google Analytics tracking helper
function trackEvent(category, action, label = '', value = null) {
  if (typeof window !== 'undefined' && typeof window.gtag === 'function') {
    const eventParams = {
      event_category: category,
      event_label: label
    };
    if (value !== null && value !== undefined) {
      eventParams.value = value;
    }
    window.gtag('event', action, eventParams);
  }
}

// Reusable objects for animation loop (avoid GC pressure)
const _raycaster = new THREE.Raycaster();
const _hitPoint = new THREE.Vector3();
const _groundPlane = new THREE.Plane(new THREE.Vector3(0, 1, 0), 0);
const _forward = new THREE.Vector3();

// Initialize from default parameters
function initFromDefaults() {
  const { camera, grid } = SceneModule;

  // Apply yaw/pitch
  applyYawPitchDeg(params.camYaw, params.camPitch);

  // Apply FOV
  camera.fov = params.camFov;
  camera.updateProjectionMatrix();

  // Apply height & camera Z
  camera.position.y = params.camHeight;
  camera.position.z = params.camZ;
  
  // scrollSpeed is now unified via getter/setter in state
  
  // Grid visibility
  grid.visible = false;

  // Scale
  pointMaterial.uniforms.uScale.value = params.scale;
  pointMaterial.uniforms.uBias.value = -params.scale * 0.5;

  // Point color default
  pointMaterial.uniforms.uColor.value.set(params.terrainColor);

  // Jitter default
  pointMaterial.uniforms.uJitter.value = params.jitter;

  // Point defaults
  pointMaterial.uniforms.uPointSize.value = params.pointSize;
  pointMaterial.uniforms.uMaskRadius.value = params.maskRadius;
  pointMaterial.uniforms.uMaskFeather.value = params.maskFeather;

  // Hover defaults
  pointMaterial.uniforms.uHoverRadius.value = params.hoverRadius;
  pointMaterial.uniforms.uHoverStrength.value = params.hoverStrength;
  pointMaterial.uniforms.uHoverSnap.value = params.hoverSnap;
  pointMaterial.uniforms.uHoverColor.value.set(params.hoverColor);
  pointMaterial.uniforms.uHoverEnabled.value = params.mouseHoverEnabled ? 1.0 : 0.0;

  // Airplane hover defaults
  pointMaterial.uniforms.uAirplaneHoverEnabled.value = params.airplaneHoverEnabled ? 1.0 : 0.0;
  pointMaterial.uniforms.uAirplaneHoverRadius.value = params.airplaneHoverRadius;

  // Dust wake effect defaults
  pointMaterial.uniforms.uDustWakeEnabled.value = params.dustWakeEnabled ? 1.0 : 0.0;
  pointMaterial.uniforms.uDustWakeRadius.value = params.dustWakeRadius;
  pointMaterial.uniforms.uDustWakeHeight.value = params.dustWakeHeight;
  pointMaterial.uniforms.uDustWakeSpeedThreshold.value = params.dustWakeSpeedThreshold;
  pointMaterial.uniforms.uDustWakeClearanceMax.value = params.dustWakeClearanceMax;
  pointMaterial.uniforms.uDustWakeTrailLength.value = params.dustWakeTrailLength;
  pointMaterial.uniforms.uDustWakeColor.value.set(params.dustWakeColor);

  // Record defaults for Reset
  setDefaultCameraState();

  // Initialize fog state
  applyFogFromParams();

  // Compile shader after defines updates
  pointMaterial.needsUpdate = true;

  // Load default heightmap texture
  const textureLoader = new THREE.TextureLoader();
  textureLoader.load(
    DEFAULT_HEIGHTMAP_URL,
    (tex) => {
      applyHeightmapTexture(tex).catch((err) => console.error('Failed to apply default heightmap', err));
    },
    undefined,
    (err) => {
      console.error('Failed to load default heightmap', err);
    }
  );
}

// Main animation loop
function animate() {
  requestAnimationFrame(animate);

  const { camera, clock, renderer } = SceneModule;

  const delta = clock.getDelta();
  pointMaterial.uniforms.uTime.value = performance.now() * 0.001;
  
  // Update hover center from mouse ray on ground plane (using pooled objects)
  _raycaster.setFromCamera(mouseNdc, camera);
  
  if (_raycaster.ray.intersectPlane(_groundPlane, _hitPoint)) {
    pointMaterial.uniforms.uHoverCenter.value.set(_hitPoint.x, _hitPoint.z);
  } else {
    pointMaterial.uniforms.uHoverCenter.value.set(9999.0, 9999.0);
  }

  // Audio-reactive terrain scale modulation
  if (isAudioReactiveActive()) {
    const energy = getAudioEnergy();
    const baseScale = params.scale;
    const modScale = baseScale + energy * params.beatReactAmplitude;
    pointMaterial.uniforms.uScale.value = modScale;
    pointMaterial.uniforms.uBias.value = -modScale * 0.5;
  } else {
    // Reset to base scale when inactive
    pointMaterial.uniforms.uScale.value = params.scale;
    pointMaterial.uniforms.uBias.value = -params.scale * 0.5;
  }

  // Update airplane FIRST so heading is current before terrain scroll
  updateAirplane(delta);

  // Update terrain scroll
  const tilesPoints = getTilesPoints();
  if (params.animateScroll && tilesPoints && tilesPoints.length) {
    // Scroll direction follows airplane heading (heading 0 = -Z direction)
    _forward.set(
      Math.sin(airplaneState.heading),
      0,
      -Math.cos(airplaneState.heading)
    );
    const step = state.scrollSpeed * delta;
    scrollOffset.x += _forward.x * step;
    scrollOffset.y += _forward.z * step;
    updateTileMatrices();
  }
  
  // Update tile LOD based on camera distance (always, even when scroll paused)
  updateTileLOD();

  // Update airplane hover position for shader
  const airplaneGroup = getAirplaneGroup();
  if (airplaneGroup && airplaneState.enabled) {
    pointMaterial.uniforms.uAirplanePos.value.copy(airplaneGroup.position);

    // Update mask center to follow airplane in chase-style modes
    const isChaseStyle = params.cameraMode === 'chase' || params.cameraMode === 'topdown';
    if (isChaseStyle) {
      pointMaterial.uniforms.uMaskCenter.value.set(0, 0);
    }

    // Update dust wake effect uniforms
    pointMaterial.uniforms.uAirplaneSpeed.value = state.scrollSpeed;
    // Calculate ground clearance for dust wake effect
    const terrainHeightAtPlane = sampleTerrainHeight(airplaneGroup.position.x, airplaneGroup.position.z);
    if (terrainHeightAtPlane !== null) {
      pointMaterial.uniforms.uAirplaneGroundClearance.value = airplaneGroup.position.y - terrainHeightAtPlane;
    } else {
      pointMaterial.uniforms.uAirplaneGroundClearance.value = 10.0; // No terrain = no dust
    }
    // Pass airplane forward direction for dust trail (inverted because terrain scrolls)
    // We want the trail to appear BEHIND the plane, which is opposite to heading direction
    pointMaterial.uniforms.uAirplaneForward.value.set(
      -Math.sin(airplaneState.heading),
      Math.cos(airplaneState.heading)
    );

    // Update UI indicators
    updateBoostUI();
  } else {
    pointMaterial.uniforms.uAirplanePos.value.set(9999, 9999, 9999);
  }

  // Update radar pulse
  updateRadarPulse();

  // Update all visual rings (multiple independent rings)
  updateVisualRings();

  // Update sonic boom effect
  updateSonicBoom(delta);

  // Update camera mode
  updateCameraMode(delta);

  // Render with post-processing composer (selective bloom)
  const composer = getComposer();
  if (composer) {
    renderSelectiveBloom();
  } else {
    // Fallback to direct render if composer not initialized
    renderer.render(scene, camera);
  }
}

// Initialize everything
function init() {
  console.log('Starting initialization...');

  // Initialize scene first (creates camera, renderer, etc.)
  console.log('Initializing scene...');
  if (!initScene()) {
    console.error('Failed to initialize scene');
    return;
  }
  console.log('Scene initialized successfully');

  // Build initial tiling
  console.log('Building terrain tiles...');
  rebuildTiling();

  // Initialize airplane
  console.log('Initializing airplane...');
  initAirplane();

  // Setup controls
  console.log('Setting up controls...');
  setupMouseControls();
  setupKeyboardControls();
  setupTouchControls();
  setupGyroControls();

  // Setup UI
  console.log('Setting up UI...');
  setupTweakpane();
  setupCrashOverlayHandler();
  initBoostUI();

  // Initialize post-processing composer
  console.log('Initializing post-processing...');
  initComposer();

  // Setup handlers
  console.log('Setting up handlers...');
  setupHeightmapHandlers();
  setupResizeHandler();
  
  // Setup resize handler for composer
  window.addEventListener('resize', () => {
    resizeComposer();
  });

  // Initialize from defaults
  console.log('Applying default settings...');
  initFromDefaults();

  console.log('Initialization complete, starting animation loop');
  // Start animation loop
  animate();
}

// Handle start screen and initialization
function setupStartScreen() {
  const startScreen = document.getElementById('start-screen');
  const modeButtons = document.querySelectorAll('.mode-btn');
  let started = false;

  // Track splash screen view
  trackEvent('Splash Screen', 'view', 'splash_screen_displayed');

  function startApp() {
    if (started) return;
    started = true;

    // Track launch
    trackEvent('Game', 'start', 'zen');

    // Hide start screen
    if (startScreen) {
      startScreen.classList.add('hidden');
    }

    // Initialize the app
    try {
      init();
    } catch (err) {
      console.error('Failed to initialize application:', err);
    }
  }

  // Handle enter button click
  if (modeButtons.length > 0) {
    modeButtons.forEach(button => {
      button.addEventListener('click', (e) => {
        e.stopPropagation();
        startApp();
      });
    });
  }

  // Listen for Enter key
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Enter' && !started) {
      startApp();
    }
  });
}

// Expose debug functions globally for console access
if (typeof window !== 'undefined') {
  window.debugBeatReactive = () => {
    const info = getAnalyzerDebugInfo();
    console.log('=== Beat-Reactive Debug Info ===');
    console.log('Active:', info.isActive);
    console.log('AudioContext:', info.hasAudioContext, 'state:', info.audioContextState);
    console.log('Analyser:', info.hasAnalyser, 'bins:', info.frequencyBinCount);
    console.log('Source Node:', info.hasSourceNode);
    console.log('Current Energy:', info.smoothedEnergy.toFixed(3));
    console.log('Base Scale:', params.scale);
    console.log('Beat Amplitude:', params.beatReactAmplitude);
    console.log('===============================');
    return info;
  };
}

// Start the application when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', setupStartScreen);
} else {
  setupStartScreen();
}
